import React, { useState, useEffect } from "react";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";

type HeatMapData = {
  [key: string]: {
    "Outperforming Nifty": number;
    "Return Difference": number;
    Strategy_perf: number;
    "Display String": string;
  };
};

const HeatMap = () => {
  const [heatmapData, setHeatmapData] = useState<{ name: string; data: any }[]>(
    []
  );
  const [tooltipData, setTooltipData] = useState<{ [key: string]: string[] }>(
    {}
  );
  const [colorRanges, setColorRanges] = useState<any[]>([]); // State for color ranges
  const [data, setData] = useState<HeatMapData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(
        (process.env.REACT_APP_STOCK_SELECTION || "") +
          `/Stock-Selection/analysis-fabric-files/Value Based Strategy/Value Based Strategy-grid.json`
      );
      const data = await response.json();
      setData(data);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };
  const cleanDisplayString = (displayString: string) => {
    // Match everything up to "NIFTY ETF--" and removingg the rest..
    const match = displayString.match(/^(.*?)<br><br>NIFTY ETF--/);
    return match ? match[1] : displayString; //for Returning the matched part or the original string
  };

  useEffect(() => {
    if (!isLoading && data) {
      const { seriesData, tooltipInfo, minValue, maxValue } =
        transformData(data);
      setHeatmapData(seriesData);
      setTooltipData(tooltipInfo);
      updateColorRange(minValue, maxValue); // Update the color scale
    }
  }, [data, isLoading]);

  const transformData = (data: any) => {
    const heatmapMap: { [key: string]: any[] } = {};
    const tooltipInfo: { [key: string]: any[] } = {};

    let minValue = Infinity;
    let maxValue = -Infinity;

    Object.keys(data).forEach((key) => {
      const [month, year] = key.split(" ");
      const {
        Strategy_perf: returnStratPerf,
        "Display String": displayString,
      } = data[key];

      //removing unnecassary info from displaystring
      const cleanedDisplayString = cleanDisplayString(displayString);

      minValue = Math.min(minValue, returnStratPerf);
      maxValue = Math.max(maxValue, returnStratPerf);

      if (!heatmapMap[year]) {
        heatmapMap[year] = Array(12).fill(null);
      }

      const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
      heatmapMap[year][monthIndex] = returnStratPerf;

      if (!tooltipInfo[year]) {
        tooltipInfo[year] = Array(12).fill(null);
      }
      tooltipInfo[year][monthIndex] = cleanedDisplayString;
    });

    const seriesData = Object.keys(heatmapMap).map((year) => ({
      name: year,
      data: heatmapMap[year].map((value, index) => ({
        x: new Date(2020, index, 1).toLocaleString("default", {
          month: "short",
        }),
        y: value,
      })),
    }));

    return { seriesData, tooltipInfo, minValue, maxValue };
  };

  const updateColorRange = (minValue: number, maxValue: number) => {
    // console.log(" View2 Min Value:", minValue, "Max Value:", maxValue);
    const newColorRanges = [
      {
        from: minValue,
        to: 0,
        name: "Negative",
        color: "#ff5e5e", // Dark red
      },
      {
        from: 0.01,
        to: maxValue,
        name: "Positive",
        color: "#059212", // Dark green
      },
      {
        from: null,
        to: null,
        name: "No Data",
        color: "#FFFFFF", // White for no data
        show: false,
      },
    ];
    setColorRanges(newColorRanges); // Update the color ranges in state
  };

  const options: ApexOptions = {
    chart: {
      height: 350,
      type: "heatmap",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#278E03"], // Default color, but will be overridden by colorScale
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.66,
        radius: 0,
        reverseNegativeShade: true,
        useFillColorAsStroke: false,
        colorScale: {
          inverse: true,
          ranges: colorRanges, // Dynamically set the color scale ranges
        },
      },
    },
    xaxis: {
      type: "category",
    },
    tooltip: {
      y: {
        formatter: (value, opts: any) => {
          const { seriesIndex, dataPointIndex } = opts;
          const year = Object.keys(tooltipData)[seriesIndex];
          if (tooltipData[year]) {
            const displayString = tooltipData[year][dataPointIndex];
            if (displayString) {
              return displayString;
            }
          }
          return value !== null && value !== undefined
            ? value.toString()
            : "N/A";
        },
      },
    },
  };

  return (
    <div>
      <ApexCharts
        options={options}
        series={heatmapData}
        type="heatmap"
        height={350}
      />
    </div>
  );
};

export default HeatMap;
